import React, { useState } from 'react';
import { animalApi } from '../../services/api';
import { handleApiError, showError, showSuccess } from '../../utils/handleApiError';

const AnimalImport: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [importResult, setImportResult] = useState<{
    message: string;
    success_count: number;
    error_count: number;
    errors: any[];
  } | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file type
      const validTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
      if (!validTypes.includes(file.type) && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        showError('Csak Excel fájlokat (.xlsx, .xls) lehet feltölteni');
        return;
      }

      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        showError('A fájl mérete nem haladhatja meg a 10 MB-ot');
        return;
      }

      setSelectedFile(file);
      setImportResult(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      showError('Kérjük válasszon ki egy fájlt');
      return;
    }

    setIsUploading(true);
    try {
      const result = await animalApi.importAnimals(selectedFile);
      setImportResult(result);

      if (result.error_count === 0) {
        showSuccess(result.message);
      } else {
        showError(`${result.success_count} állat importálva, ${result.error_count} hiba történt`);
      }
    } catch (error: any) {
      console.error('Import error:', error);
      handleApiError(error, 'Hiba történt az import során');

      if (error.response?.data?.errors) {
        setImportResult({
          message: error.response.data.message,
          success_count: error.response.data.success_count || 0,
          error_count: error.response.data.error_count || 0,
          errors: error.response.data.errors || [],
        });
      }
    } finally {
      setIsUploading(false);
    }
  };

  const resetForm = () => {
    setSelectedFile(null);
    setImportResult(null);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Állatok Importálása Excel Fájlból</h2>

        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Várt Oszlopok:</h3>
          <div className="bg-gray-50 rounded p-4">
            <ul className="grid grid-cols-2 gap-2 text-sm text-gray-600">
              <li><span className="font-medium">Országkód</span> - Állat országkódja (pl. HU)</li>
              <li><span className="font-medium">Fülszám</span> - Fülszám (kötelező)</li>
              <li><span className="font-medium">Születés</span> - Születési dátum</li>
              <li><span className="font-medium">Beszállítás dátuma</span> - Érkezés dátuma</li>
              <li><span className="font-medium">Anya Országkód</span> - Anyaállat országkódja</li>
              <li><span className="font-medium">Anya Fülszám</span> - Anyaállat fülszáma</li>
              <li><span className="font-medium">Származási Ország</span> - Származási ország</li>
              <li><span className="font-medium">Súly</span> - Beszerzési súly (kg)</li>
              <li><span className="font-medium">Eladó</span> - Eladó neve</li>
            </ul>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            * Az Excel fájlnak fejléc sorral kell rendelkeznie a fenti oszlopnevekkel
          </p>
        </div>

        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8">
          <div className="text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <div className="mt-4">
              <label
                htmlFor="file-upload"
                className="relative cursor-pointer bg-white rounded-md font-medium text-green-600 hover:text-green-500 focus-within:outline-none"
              >
                <span>Válasszon fájlt</span>
                <input
                  id="file-upload"
                  name="file-upload"
                  type="file"
                  accept=".xlsx,.xls"
                  className="sr-only"
                  onChange={handleFileChange}
                />
              </label>
              <p className="pl-1 text-gray-500">vagy húzza ide a fájlt</p>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Excel fájl (XLSX, XLS) maximum 10 MB
            </p>
          </div>

          {selectedFile && (
            <div className="mt-4 bg-green-50 border border-green-200 rounded p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                    <p className="text-xs text-gray-500">{(selectedFile.size / 1024).toFixed(2)} KB</p>
                  </div>
                </div>
                <button
                  onClick={resetForm}
                  className="text-red-600 hover:text-red-800"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            type="button"
            onClick={resetForm}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Mégse
          </button>
          <button
            type="button"
            onClick={handleImport}
            disabled={!selectedFile || isUploading}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isUploading ? 'Importálás...' : 'Importálás'}
          </button>
        </div>
      </div>

      {/* Import Results */}
      {importResult && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Import Eredmények</h3>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-green-50 border border-green-200 rounded p-4">
              <p className="text-sm text-gray-600">Sikeresen importálva</p>
              <p className="text-3xl font-bold text-green-600">{importResult.success_count}</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded p-4">
              <p className="text-sm text-gray-600">Hibák száma</p>
              <p className="text-3xl font-bold text-red-600">{importResult.error_count}</p>
            </div>
          </div>

          {importResult.errors && importResult.errors.length > 0 && (
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3">Hibák részletei:</h4>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {importResult.errors.map((error, index) => (
                  <div key={index} className="bg-red-50 border border-red-200 rounded p-3">
                    <p className="text-sm font-medium text-red-800">
                      {error.row ? `Sor ${error.row}:` : 'Hiba:'}
                    </p>
                    <p className="text-sm text-red-600 mt-1">
                      {error.error || error.errors?.join(', ') || 'Ismeretlen hiba'}
                    </p>
                    {error.attribute && (
                      <p className="text-xs text-red-500 mt-1">Mező: {error.attribute}</p>
                    )}
                    {error.values && (
                      <p className="text-xs text-gray-600 mt-1">
                        Értékek: {JSON.stringify(error.values)}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AnimalImport;
